'use client';
import { useState } from 'react';
import { useSearchParams } from 'next/navigation';
import { useRouter } from 'next/navigation';
import { signIn } from 'next-auth/react';
import Link from 'next/link';
import classNames from 'classnames';

export default function SigninForm() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const redirect = searchParams.get('redirect') || '/dashboard'; // Default to dashboard if no redirect is provided
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    const res = await signIn('credentials', {
      email,
      password,
      redirect: false, // Prevent NextAuth from redirecting automatically
    });

    if (res?.error) {
      setError(
        res.error === 'CredentialsSignin'
          ? 'Invalid email or password'
          : res.error
      );
      setLoading(false);
      return;
    }

    router.push(redirect); // Redirect to the original intent
  };

  return (
    <div className="glass-effect rounded-xl p-8 shadow-lg backdrop-blur-md border border-white/10 bg-gray-900/70">
      <h1 className="text-3xl font-bold mb-6 text-white text-center">
        Sign In
      </h1>

      {error && (
        <div className="bg-red-500/20 border border-red-500/50 text-white rounded-lg p-3 mb-4">
          {error}
        </div>
      )}

      <form onSubmit={handleSignIn} className="space-y-4">
        <div>
          <label className="block text-gray-200 mb-1 text-sm font-medium">
            Email
          </label>
          <input
            type="email"
            placeholder="your@email.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full p-3 bg-gray-800/80 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all"
            required
          />
        </div>

        <div>
          <label className="block text-gray-200 mb-1 text-sm font-medium">
            Password
          </label>
          <input
            type="password"
            placeholder="••••••••"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full p-3 bg-gray-800/80 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all"
            required
          />
        </div>

        <button
          type="submit"
          disabled={loading}
          className={classNames(
            'w-full p-3 mt-2 rounded-lg font-medium transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500',
            loading
              ? 'bg-gradient-to-r from-indigo-600/60 to-violet-500/60 text-white/80 relative overflow-hidden'
              : 'bg-gradient-to-r from-indigo-600 to-violet-500 hover:from-indigo-700 hover:to-violet-600 text-white shadow-lg hover:shadow-indigo-500/20 transform hover:scale-105'
          )}
        >
          {loading ? (
            <span className="flex items-center justify-center">
              <svg
                className="animate-spin -ml-1 mr-3 h-5 w-5 text-white/80"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              Signing In...
            </span>
          ) : (
            'Sign In'
          )}
          {loading && (
            <span className="absolute inset-0 h-full w-full bg-gradient-to-r from-indigo-600/10 to-violet-500/10 animate-pulse"></span>
          )}
        </button>

        <div className="mt-6 space-y-3 text-center">
          <p className="text-gray-200">
            Don&apos;t have an account?{' '}
            <Link
              href={`/auth/signup?redirect=${encodeURIComponent(redirect)}`}
              className="text-indigo-300 hover:text-indigo-200 font-medium transition-colors"
            >
              Create one here
            </Link>
          </p>

          <p className="text-gray-200">
            Forgot your password?{' '}
            <Link
              href="/auth/request-reset"
              className="text-indigo-300 hover:text-indigo-200 font-medium transition-colors"
            >
              Reset password
            </Link>
          </p>
        </div>
      </form>
    </div>
  );
}
